import type { Brand, OperatorGroup } from '~portal/data/types';
import {
  applyFilters,
  type ListingOptions,
  type ListingSearch,
  matchesQuery,
  numberParam,
  parseListingSearch,
  type ResolvedListing,
  resolveListingSearch,
  sortBy,
  stringsParam,
} from '~portal/lib/listing';

/**
 * Pure state and data rules for the operators listing (`/operators`): the URL search it keeps,
 * the filters, the sort and the tab logic. The page renders from these and nothing else, so the
 * rules are pinned by unit tests rather than by rendering.
 *
 * The URL is the state (so a filtered view can be shared or bookmarked), and every default is
 * omitted, so the bare `/operators` is the default view: the brand tab, list view (client
 * decision, 14 Sep), highest GGR first (D-6, until a real `updatedAt` exists), 24 per page.
 *
 * The filters are Figma's three (D-4, O8): parent groups, operating locations and a GGR range.
 * The earlier "Entity type" group is gone with its URL param.
 */

export const OPERATOR_TABS = ['brand', 'group', 'within'] as const;
export type OperatorTab = (typeof OPERATOR_TABS)[number];

/** @desc Figma's labels (`301:58186`), as the Account tabs took theirs. */
export const TAB_LABELS: Record<OperatorTab, string> = {
  brand: 'By Brand',
  group: 'By Group',
  within: 'Within Group',
};

export const DEFAULT_TAB: OperatorTab = 'brand';

export const OPERATOR_SORTS = ['ggr', 'name', 'growth'] as const;
export type OperatorSort = (typeof OPERATOR_SORTS)[number];

export const SORT_OPTIONS: ReadonlyArray<{ value: OperatorSort; label: string }> = [
  { value: 'ggr', label: 'Online GGR (high–low)' },
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'growth', label: 'YoY growth (high–low)' },
];

export const LISTING_OPTIONS: ListingOptions<OperatorSort> = {
  sorts: OPERATOR_SORTS,
  defaultSort: 'ggr',
};

/**
 * @desc The listing's URL search. `ggrMin` / `ggrMax` are online GGR in **USD millions** — the
 * one currency every brand and group can be compared in, at a grain that keeps the URL short.
 * On the "Within group" tab the chosen group is `group[0]`: picking a group there is the same
 * narrowing as filtering to one parent group, so it shares the param rather than adding another.
 */
export type OperatorsSearch = ListingSearch<OperatorSort> & {
  tab?: Exclude<OperatorTab, 'brand'>;
  group?: string[];
  location?: string[];
  ggrMin?: number;
  ggrMax?: number;
};

/** @desc A min/max pair in USD millions; an absent bound is no bound. */
export type GgrRange = { min?: number; max?: number };

/** @desc The listing's own filter fields, with defaults filled in — also the filter sheet's draft. */
export type OperatorFilters = {
  group: string[];
  location: string[];
  ggr: GgrRange;
};

export type ResolvedOperatorsSearch = ResolvedListing<OperatorSort> &
  OperatorFilters & { tab: OperatorTab };

/** @desc The route's `validateSearch`: keeps what is valid, drops the rest, omits defaults. */
export const parseOperatorsSearch = (raw: Record<string, unknown>): OperatorsSearch => {
  const search: OperatorsSearch = parseListingSearch(raw, LISTING_OPTIONS);
  const tab = OPERATOR_TABS.find((t) => t === raw.tab);
  if (tab && tab !== DEFAULT_TAB) search.tab = tab;
  const group = stringsParam(raw.group);
  if (group.length > 0) search.group = group;
  const location = stringsParam(raw.location);
  if (location.length > 0) search.location = location;
  const ggrMin = numberParam(raw.ggrMin);
  const ggrMax = numberParam(raw.ggrMax);
  if (ggrMin !== undefined && ggrMin > 0) search.ggrMin = ggrMin;
  if (ggrMax !== undefined && ggrMax >= 0) search.ggrMax = ggrMax;
  // A reversed range is a hand-edited URL; swap it rather than show nothing.
  if (search.ggrMin !== undefined && search.ggrMax !== undefined && search.ggrMin > search.ggrMax) {
    [search.ggrMin, search.ggrMax] = [search.ggrMax, search.ggrMin];
  }
  return search;
};

export const resolveOperatorsSearch = (search: OperatorsSearch): ResolvedOperatorsSearch => ({
  ...resolveListingSearch(search, LISTING_OPTIONS),
  tab: search.tab ?? DEFAULT_TAB,
  group: search.group ?? [],
  location: search.location ?? [],
  ggr: {
    ...(search.ggrMin !== undefined ? { min: search.ggrMin } : {}),
    ...(search.ggrMax !== undefined ? { max: search.ggrMax } : {}),
  },
});

/**
 * @desc A change to the listing's URL state. `null` clears a field. Any change but a page change
 * returns the reader to page 1 (the shared listing rule).
 */
export type OperatorsSearchPatch = Partial<
  Omit<ResolvedOperatorsSearch, 'ggr'> & {
    ggrMin: number | null;
    ggrMax: number | null;
  }
>;

export const withOperatorsSearch = (
  current: OperatorsSearch,
  patch: OperatorsSearchPatch,
): OperatorsSearch => {
  const merged: Record<string, unknown> = { ...current, ...patch };
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) delete merged[key];
  }
  // A page number means nothing once the set or its order has changed.
  if (Object.keys(patch).some((key) => key !== 'page')) delete merged.page;
  return parseOperatorsSearch(merged);
};

/** @desc The sheet's draft as a URL patch: an absent bound clears its param. */
export const filtersPatch = (filters: OperatorFilters): OperatorsSearchPatch => ({
  group: filters.group,
  location: filters.location,
  ggrMin: filters.ggr.min ?? null,
  ggrMax: filters.ggr.max ?? null,
});

const hasRange = (ggr: GgrRange) => ggr.min !== undefined || ggr.max !== undefined;

/** @desc The filters that narrow the set, counted for "Filters applied (n)" (search excluded). */
export const activeFilterCount = (search: ResolvedOperatorsSearch): number =>
  [
    // On "Within group" the group is the tab's own selector, not a filter.
    search.tab !== 'within' && search.group.length > 0,
    search.location.length > 0,
    hasRange(search.ggr),
  ].filter(Boolean).length;

/** @desc Whether the record count should read "Filtering for m of n". */
export const isFiltered = (search: ResolvedOperatorsSearch): boolean =>
  activeFilterCount(search) > 0 || search.q.length > 0;

/** @desc Every filter off; what the sheet's "Clear filters" commits. Keeps the within-group selection. */
export const clearedFilters = (
  search: Pick<ResolvedOperatorsSearch, 'tab' | 'group'>,
): OperatorFilters => ({
  group: search.tab === 'within' ? search.group.slice(0, 1) : [],
  location: [],
  ggr: {},
});

/** @desc Clears every filter and the search, keeping tab, view, sort and items per page. */
export const clearFiltersPatch = (search: ResolvedOperatorsSearch): OperatorsSearchPatch => ({
  ...filtersPatch(clearedFilters(search)),
  q: '',
});

// ---------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------

/** @desc A brand row: the brand plus the group it links to (brands have no page of their own). */
export type BrandRow = { brand: Brand; group: OperatorGroup };

export const brandRows = (groups: readonly OperatorGroup[]): BrandRow[] =>
  groups.flatMap((group) => group.brands.map((brand) => ({ brand, group })));

/**
 * @desc A brand's comparable GGR in USD, or `undefined` for a B2B supplier — it earns revenue,
 * not GGR, so it shows "—" and sorts last rather than as a zero.
 */
export const brandGgrUsd = (brand: Brand): number | undefined =>
  brand.type === 'supplier-b2b' ? undefined : brand.onlineGgrUsd;

/** @desc A group's comparable GGR in USD; absent for a sold group (no financials). */
export const groupGgrUsd = (group: OperatorGroup): number | undefined =>
  group.status === 'sold' ? undefined : group.financials?.onlineGgrUsd;

const inRange = (usd: number | undefined, ggr: GgrRange) => {
  // A range filter matches figures inside it; a missing figure is inside no range.
  if (usd === undefined) return false;
  const millions = usd / 1_000_000;
  return (
    (ggr.min === undefined || millions >= ggr.min) && (ggr.max === undefined || millions <= ggr.max)
  );
};

/**
 * @desc Live licensed presence (`markets.list` rows' `operatorsPresent`): market slug → the portal
 * group slugs the licensed-entity data credits there. Only markets with licensed-entity rows are
 * keys (Denmark and Croatia on staging today); every other market is unknown, not empty.
 */
export type LivePresence = ReadonlyMap<string, readonly string[]>;

const NO_LIVE_PRESENCE: LivePresence = new Map();

/**
 * @desc Whether a group operates in any of `locations`: from the live presence where the market has
 * licensed-entity rows (which then overrules the fixture both ways), from the fixture's
 * `operatingRegions` everywhere else. Presence is group-level, so a brand is present where its
 * group is.
 */
const operatesIn = (
  groupSlug: string,
  fixtureRegions: readonly string[],
  locations: readonly string[],
  live: LivePresence,
) =>
  locations.some((location) => {
    const present = live.get(location);
    return present ? present.includes(groupSlug) : fixtureRegions.includes(location);
  });

export const filterBrandRows = (
  rows: readonly BrandRow[],
  search: OperatorFilters & { q: string },
  live: LivePresence = NO_LIVE_PRESENCE,
): BrandRow[] =>
  applyFilters(rows, [
    search.group.length > 0 && ((r) => search.group.includes(r.group.slug)),
    search.location.length > 0 &&
      ((r) => operatesIn(r.group.slug, r.brand.operatingRegions, search.location, live)),
    hasRange(search.ggr) && ((r) => inRange(brandGgrUsd(r.brand), search.ggr)),
    search.q.length > 0 &&
      ((r) => matchesQuery(search.q, r.brand.name, r.group.name, r.brand.hqMarket)),
  ]);

export const filterGroups = (
  groups: readonly OperatorGroup[],
  search: OperatorFilters & { q: string },
  live: LivePresence = NO_LIVE_PRESENCE,
): OperatorGroup[] =>
  applyFilters(groups, [
    search.group.length > 0 && ((g) => search.group.includes(g.slug)),
    search.location.length > 0 &&
      ((g) => operatesIn(g.slug, g.operatingRegions, search.location, live)),
    hasRange(search.ggr) && ((g) => inRange(groupGgrUsd(g), search.ggr)),
    search.q.length > 0 &&
      ((g) => matchesQuery(search.q, g.name, g.hqMarket, ...g.brands.map((b) => b.name))),
  ]);

export const sortBrandRows = (rows: readonly BrandRow[], sort: OperatorSort): BrandRow[] => {
  switch (sort) {
    case 'name':
      return sortBy(rows, (r) => r.brand.name);
    case 'growth':
      return sortBy(rows, (r) => r.brand.yoyGrowth, 'desc');
    case 'ggr':
      return sortBy(rows, (r) => brandGgrUsd(r.brand), 'desc');
  }
};

export const sortGroups = (groups: readonly OperatorGroup[], sort: OperatorSort) => {
  switch (sort) {
    case 'name':
      return sortBy(groups, (g) => g.name);
    case 'growth':
      return sortBy(
        groups,
        (g) => (g.status === 'sold' ? undefined : g.financials?.yoyGrowth),
        'desc',
      );
    case 'ggr':
      return sortBy(groups, groupGgrUsd, 'desc');
  }
};

/** @desc The group a "Within group" view shows — the first chosen group, if it exists. */
export const withinGroup = (
  groups: readonly OperatorGroup[],
  search: Pick<OperatorFilters, 'group'>,
): OperatorGroup | undefined => groups.find((g) => g.slug === search.group[0]);

/**
 * @desc The whole set a tab counts against ("Filtering for m of n"): every brand, every group,
 * or the chosen group's brands.
 */
export const tabUniverse = (
  groups: readonly OperatorGroup[],
  search: ResolvedOperatorsSearch,
): { brands: BrandRow[]; groups: OperatorGroup[] } => {
  if (search.tab === 'group') return { brands: [], groups: [...groups] };
  if (search.tab === 'within') {
    const group = withinGroup(groups, search);
    return { brands: group ? brandRows([group]) : [], groups: [] };
  }
  return { brands: brandRows(groups), groups: [] };
};

/**
 * @desc The rows the current tab shows, filtered and sorted. On "Within group" the group param
 * is the tab's selector, so it is not applied a second time as a filter.
 */
export const visibleRows = (
  groups: readonly OperatorGroup[],
  search: ResolvedOperatorsSearch,
  live: LivePresence = NO_LIVE_PRESENCE,
): { brands: BrandRow[]; groups: OperatorGroup[]; total: number } => {
  const universe = tabUniverse(groups, search);
  if (search.tab === 'group') {
    return {
      brands: [],
      groups: sortGroups(filterGroups(universe.groups, search, live), search.sort),
      total: universe.groups.length,
    };
  }
  const filters = search.tab === 'within' ? { ...search, group: [] } : search;
  return {
    brands: sortBrandRows(filterBrandRows(universe.brands, filters, live), search.sort),
    groups: [],
    total: universe.brands.length,
  };
};

/**
 * @desc The "Operating locations" picker: the region slugs the operator fixtures mention plus every
 * market where the live presence credits at least one portal group. Resolved to live market names
 * by the page; a slug the live list lacks keeps the fallback name.
 */
export const operatingLocationOptions = (
  groups: readonly OperatorGroup[],
  nameOf: (slug: string) => string,
  live: LivePresence = NO_LIVE_PRESENCE,
): Array<{ value: string; label: string }> =>
  [
    ...new Set([
      ...groups.flatMap((g) => g.operatingRegions),
      ...[...live].filter(([, present]) => present.length > 0).map(([slug]) => slug),
    ]),
  ]
    .map((slug) => ({ value: slug, label: nameOf(slug) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'en-GB', { sensitivity: 'base' }));

/** @desc The live presence from the market directory: only markets whose row carries it. */
export const livePresenceOf = (
  markets: Iterable<{ slug: string; operatorsPresent?: readonly string[] }>,
): LivePresence =>
  new Map(
    [...markets].flatMap((m) =>
      m.operatorsPresent ? [[m.slug, m.operatorsPresent] as const] : [],
    ),
  );
